import { Select, createStyles, rem } from "@mantine/core";
import { useEffect, useState } from "react";
import { FiChevronLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import styled from "styled-components";
import { BiService } from "../../services/bi/bi.service";
import { Lesson, LessonService } from "../../services/lesson/lesson.service";
import { Chart, ChartPanel, FiltersPanel, PageContent, PageHeader, PageWrapper } from "./styles";

const useStyles = createStyles((theme) => ({
  root: { position: "relative", marginBottom: rem(2.4) },
  input: { height: rem(56), paddingTop: rem(18) },
  label: {
    position: "absolute",
    pointerEvents: "none",
    fontSize: theme.fontSizes.xs,
    paddingLeft: theme.spacing.sm,
    paddingTop: `calc(${theme.spacing.sm} / 2)`,
    zIndex: 1,
  },
}));

const questions = [
  { key: "studied-bible-and-lesson-daily", label: "Quantos estudaram diariamente a bíblia e a lição?" },
  { key: "participated-in-small-group-this-week", label: "Quantos participaram de um pequeno grupo nesta semana?" },
  { key: "gave-bible-study-this-week", label: "Quantos deram algum estudo bíblico nesta semana?" },
  { key: "carried-out-another-missionary-activity", label: "Quantos realizaram alguma outra atividade missionária utilizando seu talento?" },
];

type TSeries = { name: string; type: string; stack: string; data: number[]; lessons: string[] };

export function RelacaoPerguntas() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [questionSelected, setQuestionSelected] = useState<string | null>(null);
  const [lessonSelected, setLessonSelected] = useState<string | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>(new Array<Lesson>());
  const [series, setSeries] = useState<TSeries[]>(new Array<TSeries>());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<string[]>(new Array<string>());

  const option = {
    tooltip: { trigger: "axis" },
    legend: {},
    grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
    toolbox: { feature: { saveAsImage: {} } },
    xAxis: { type: "category", boundaryGap: true, data },
    yAxis: {
      type: "category",
      boundaryGap: false,
      data: Array.from({ length: Math.max(...series.map((s) => Math.max(...s.data))) + 4 }).map((_, i) => i),
    },
    series,
  };

  const fetch = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const responseLessons = await LessonService.getAtDate();
      let years = [...new Set(responseLessons.map((l) => l.year))];
      const trimestresPorAno = years.map((y) => ({
        year: y,
        quarters: [...new Set(responseLessons.filter((l) => l.year === y).map((r) => r.quarter))],
      }));
      let newLessonsList: any[] = [];
      trimestresPorAno.forEach((item) => {
        newLessonsList = newLessonsList.concat(item.quarters.map((q) => ({ year: item.year, quarter: q })));
      });
      setLessons(newLessonsList);
    } catch (error: any) {
      toast.warning(error[0], { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      if (lessonSelected) {
        const [anoS, trimestreS] = lessonSelected.split(";");
        const year = Number(anoS.replace(/[^0-9]/g, ""));
        const quarter = Number(trimestreS.replace(/[^0-9]/g, ""));
        const question_key = questionSelected
          ? (questions.find((q) => q.label === questionSelected)?.key as string)
          : "";

        const response = await BiService.getQuestionAnswerIndex({ question_key, year, quarter });

        if (response?.items?.length) {
          let listLessons: string[] = [];
          const seriesData: any[] = [];
          response.items.forEach((i) => {
            listLessons = [...listLessons, ...i.result.map((r) => r.key)];
            seriesData.push({ name: i.label, type: "line", stack: "Total", data: i.result.map((r) => r.value), lessons: i.result.map((r) => r.key) });
          });
          const l = [...new Set(listLessons)]
            .map((i) => i.replace(/[^0-9]/g, ""))
            .sort((a, b) => Number(a) - Number(b))
            .map((i) => `Lição ${i}`);
          setData(l);
          setSeries(seriesData);
        } else {
          toast.warning("Não há dados para serem exibidos", { position: toast.POSITION.TOP_RIGHT });
          setSeries([]);
        }
      }
    } catch (error: any) {
      toast.warning(error[0], { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  return (
    <PageWrapper>
      <PageHeader onClick={() => navigate(-1)}>
        <FiChevronLeft />
        <span>Totalizador de perguntas</span>
      </PageHeader>

      <PageContent>
        <FiltersPanel>
          <Select
            mt="md"
            withinPortal
            data={questions.map((l) => l.label)}
            placeholder="Selecione uma pergunta"
            label="Pergunta"
            classNames={classes}
            onChange={setQuestionSelected}
            disabled={isLoading}
          />
          <Select
            mt="md"
            withinPortal
            data={lessons.map((l) => `Ano: ${l.year}; Trimestre: ${l.quarter}`)}
            placeholder="Selecione um trimestre"
            label="Trimestre"
            classNames={classes}
            onChange={setLessonSelected}
            disabled={isLoading}
          />
          <SearchButton onClick={fetchData} disabled={isLoading}>
            {isLoading ? <Spinner color="light" style={{ width: 18, height: 18 }} /> : <label style={{ cursor: "pointer" }}>Buscar</label>}
          </SearchButton>
        </FiltersPanel>

        <ChartPanel>
          <Chart option={option} />
        </ChartPanel>
      </PageContent>
    </PageWrapper>
  );
}

const SearchButton = styled.button`
  height: 3.5rem;
  border-radius: 0.4rem;
  background-color: #3439a5;
  border: 0.1rem solid #2b2fa3;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.8rem;

  label {
    color: #fff;
    font-size: 1.4rem;
    font-weight: bold;
  }

  &:disabled { opacity: 0.6; }
`;
